/**
 * Loading the vendored models.
 *
 * Everything comes from this origin. Files too big for the host arrive in parts
 * (see `scripts/vendor-assets.mjs`) and are stitched back together here, then
 * checked against the SHA-256 recorded at vendor time — a truncated part or a
 * stale cache entry fails loudly instead of feeding a model garbage weights.
 */

export interface AssetEntry {
  name: string;
  bytes: number;
  sha256: string;
  parts: string[];
}

const BASE = import.meta.env.BASE_URL;

/** Where each kind of asset is published. */
const DIRS: Record<string, string> = {
  'ort-wasm-simd-threaded.asyncify.wasm': `${BASE}vendor/ort/`,
};
const MODEL_DIR = `${BASE}models/`;

export const TASKS_WASM_DIR = `${BASE}vendor/tasks-vision/${__TASKS_VISION_VERSION__}`;

export type LoadProgress = (loaded: number, total: number) => void;

let manifest: Promise<Record<string, AssetEntry>> | null = null;

function readManifest(): Promise<Record<string, AssetEntry>> {
  manifest ??= fetch(`${MODEL_DIR}manifest.json`).then((r) => {
    if (!r.ok) throw new Error('The model manifest is missing. Run `npm run vendor`.');
    return r.json() as Promise<Record<string, AssetEntry>>;
  });
  return manifest;
}

async function hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

const inflight = new Map<string, Promise<ArrayBuffer>>();

/**
 * Fetch an asset whole, however many parts it was published in.
 *
 * Parts are fetched concurrently and progress is reported in bytes, so a first
 * launch on a slow connection can say how far along it is rather than spin.
 */
export function loadAsset(name: string, onProgress?: LoadProgress): Promise<ArrayBuffer> {
  const existing = inflight.get(name);
  if (existing) return existing;

  const task = (async () => {
    const entry = (await readManifest())[name];
    if (!entry) throw new Error(`${name} is not in the model manifest.`);
    const dir = DIRS[name] ?? MODEL_DIR;
    let loaded = 0;

    const chunks = await Promise.all(
      entry.parts.map(async (part) => {
        const res = await fetch(`${dir}${part}`);
        if (!res.ok || !res.body) throw new Error(`${part}: ${res.status}`);
        const reader = res.body.getReader();
        const pieces: Uint8Array[] = [];
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          pieces.push(value);
          loaded += value.byteLength;
          onProgress?.(loaded, entry.bytes);
        }
        return pieces;
      }),
    );

    const out = new Uint8Array(entry.bytes);
    let offset = 0;
    for (const pieces of chunks) {
      for (const piece of pieces) {
        out.set(piece, offset);
        offset += piece.byteLength;
      }
    }
    if (offset !== entry.bytes) throw new Error(`${name} arrived short: ${offset} of ${entry.bytes} bytes.`);
    if ((await hex(out.buffer)) !== entry.sha256) throw new Error(`${name} failed its checksum.`);
    return out.buffer;
  })();

  inflight.set(name, task);
  // A failed load must be retryable, not cached as a rejection forever.
  task.catch(() => inflight.delete(name));
  return task;
}

/**
 * Whether every part of an asset is already in the service worker's cache —
 * i.e. whether using it would cost the person any data. False when there is no
 * cache to ask (development, or a browser without one); that only means the
 * app offers a download it did not strictly need to.
 */
export async function isAssetCached(name: string): Promise<boolean> {
  if (!('caches' in self)) return false;
  try {
    const entry = (await readManifest())[name];
    if (!entry) return false;
    const dir = DIRS[name] ?? MODEL_DIR;
    const hits = await Promise.all(entry.parts.map((part) => caches.match(`${dir}${part}`)));
    return hits.every(Boolean);
  } catch {
    return false;
  }
}
